import dayjs from 'dayjs';
import ru from 'dayjs/locale/ru';
import kk from 'dayjs/locale/kk';
import en from 'dayjs/locale/en';
import updateLocale from 'dayjs/plugin/updateLocale';

import { appLocale } from '../model';

dayjs.locale(ru); // Use ru locale by default

const customLocales = (isShortKK?: boolean) => ({
  ru: {
    day: 'д',
    h: 'ч',
    m: 'м',
  },
  kk: {
    day: isShortKK ? 'к' : 'күн',
    h: isShortKK ? 'с' : 'сағ',
    m: isShortKK ? 'м' : 'мин',
  },
  en: {
    day: 'd',
    h: 'h',
    m: 'm',
  },
});

export const setLocale = (
  locale: appLocale.config.Locale = appLocale.config.locales.RU,
) => {
  let dayjsLocale;
  if (locale === appLocale.config.locales.KK) {
    dayjs.extend(updateLocale);
    dayjs.updateLocale('kk', {
      weekdaysMin: ['жс', 'дс', 'сс', 'сә', 'бс', 'жм', 'сб'],
    });
    dayjsLocale = kk;
  } else if (locale === appLocale.config.locales.EN) {
    dayjs.extend(updateLocale);
    dayjs.updateLocale('en', {
      weekdaysMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    });
    dayjsLocale = en;
  } else {
    dayjsLocale = ru;
  }
  dayjs.locale(dayjsLocale);
};

export const removeTimezone = (t: string) => t.replace(/[+-]\d{2}:\d{2}$/g, '');

export const format = (date: dayjs.ConfigType, format: string) =>
  dayjs(date).format(format);

export const getVerbalDate = (date: dayjs.ConfigType) => {
  const [day, month] = format(date, 'D:MMM').split(':');
  return `${day} ${month.slice(0, 3).replace('май', 'мая')}`;
};

export const getPrettyDayOfWeek = (date: dayjs.ConfigType) =>
  format(date, 'dd');

export const prettyTime = (date: dayjs.ConfigType) => format(date, 'HH:mm');

export const prettyDurationFromSeconds = (
  secondsStr: string | number,
  isShortKK?: boolean,
) => {
  const seconds = Number(secondsStr);
  const days = Math.floor(seconds / 3600 / 24);
  let hours = Math.floor((seconds / 3600) % 24);
  let minutes = Math.round((seconds % 3600) / 60);

  const currentLocale = dayjs.locale();
  let durationLocale = customLocales(isShortKK).ru;
  if (currentLocale === appLocale.config.locales.KK) {
    durationLocale = customLocales(isShortKK).kk;
  } else if (currentLocale === appLocale.config.locales.EN) {
    durationLocale = customLocales(isShortKK).en;
  }

  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }

  const dDisplay = days > 0 ? `${days} ${durationLocale.day} ` : '';
  const hDisplay = hours > 0 ? `${hours} ${durationLocale.h} ` : '';
  const mDisplay = minutes > 0 ? `${minutes} ${durationLocale.m}` : '';
  return `${dDisplay}${hDisplay}${mDisplay}`;
};

export const getTomorrowDate = () => dayjs().add(1, 'd').format('YYYY-MM-DD');

export const getFutureDate = (daysAhead: number) =>
  dayjs().add(daysAhead, 'd').format('YYYY-MM-DD');

export const dateToAccountsFormat = (date: string) =>
  date.split('.').reverse().join('-');
export const dateFromAccountsFormat = (date: string) =>
  date.split('-').reverse().join('.');

export function getAgeByDate(birthDate: string, dateToCompare: string) {
  const formattedDateToCompare = dayjs(dateToCompare).format('YYYY-MM-DD');
  /**
   * Parse date in a few formats
   * @see https://day.js.org/docs/en/parse/string-format
   */
  const birth = dayjs(birthDate, ['DD.MM.YYYY', 'DD-MM-YYYY']).format(
    'YYYY-MM-DD',
  );

  /** Should use same format of dates to get precise difference between dates */
  const age = dayjs(formattedDateToCompare).diff(birth, 'y', true);

  return {
    age,
    isPaxBornByDepartureDate: dayjs(dateToCompare).isSameOrAfter(birth),
  };
}

export function getFormattedDateAndWeekDay(date: string) {
  return `${getVerbalDate(date)}, ${getPrettyDayOfWeek(date)}`;
}

export function getFormattedDateAndTime(date: string) {
  return `${format(date, 'D MMMM, HH:mm')}`;
}

export function toUnix(date: string | number | Date | dayjs.Dayjs) {
  return dayjs(date).unix();
}
